<template>
    <div id="main-header" class="fixed z-[65] top-0 left-0 border-b border-basic-2 px-8 w-full h-[4rem] flex justify-end items-center gap-2 bg-background-basic-1">
        <ElementsTooltipSingleton
            :ref="
                (el) => {
                    loadTooltipElementReady(el);
                }
            " />
        <!-- Testing tool settings for local and staging only -->
        <a
            class="border border-basic-3 w-[2.75rem] h-[2.75rem] bg-white flex justify-center items-center rounded-2 transition-all duration-500s hover:bg-background-basic-2"
            href="javascript:void(0)"
            v-show="enableTestTools() === true"
            @click="clickTestingTools">
            <CogIcon class="h-6 w-6" aria-hidden="true" />
        </a>
        <div
            v-if="showManualOver"
            class="fixed inset-0 z-40 bg-black/40 transition-opacity duration-300"
        ></div>
        <!-- 알람 이미지 -->
        <DashboardNotifications v-model:open="notificationList.show" v-model:arriveNewNews="notificationList.countNewNews" />
        <DashboardManual v-model:open="showManualOver" />
        <div class="cursor-pointer border rounded-2 border-primary-3 px-2 h-[2.75rem] flex justify-center items-center gap-1 transition-all duration-500 hover:bg-primary-1" @click="clickViewManual">
            <img src="@/assets/img/Book-mark.svg" alt="" class="w-6" />
            <div class="text-primary-3 font-body-md-medium tracking-wide">Manual</div>
        </div>
        <Menu as="div" class="relative inline-block text-left">
            <div>
                <MenuButton
                    class="flex justify-between w-full h-[2.75rem] rounded-2 border transition-all hover:bg-background-basic-2 items-center gap-1 duration-500 border-basic-3 px-[0.75rem]">
                    <div class="w-7 h-7 inline-flex rounded-full bg-zinc-100 items-center overflow-hidden">
                        <img v-if="profilePhoto" :src="profilePhoto" class="object-cover w-full h-full object-center" alt="main-header-1" />
                        <div v-else class="inline-flex items-center justify-center h-7 w-7 rounded-full bg-content-basic-3 pb-[1px]">
                            <span v-if="username" class="font-body-md-semibold leading-none text-white flex justify-center items-center">{{ profileString }}</span>
                        </div>
                    </div>
                    <span class="flex-1 font-body-md-regular" v-text="username"></span>
                    <ChevronDownIcon class="-mr-1 h-5 w-5" aria-hidden="true" />
                </MenuButton>
            </div>

            <transition
                enter-active-class="transition ease-out duration-100"
                enter-from-class="transform opacity-0 scale-95"
                enter-to-class="transform opacity-100 scale-100"
                leave-active-class="transition ease-in duration-75"
                leave-from-class="transform opacity-100 scale-100"
                leave-to-class="transform opacity-0 scale-95">
                <MenuItems class="fixed right-[2rem] mt-2 w-[12rem] rounded-2 shadow-card bg-white ring-1 ring-black ring-opacity-5 overflow-hidden">
                    <div>
                        <MenuItem v-slot="{ active }">
                            <a
                                href="javascript:void(0)"
                                :class="[
                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                    'px-4 font-body-md-semibold leading-[3.5rem] flex gap-2 transition-all duration-500s hover:bg-background-basic-2',
                                ]"
                                @click="openMyAccountPopup">
                                <PersonIcon :width="24" :height="24" color="text-[#2E2C34]" weight="REGULAR" style="margin-top:1rem" /><span>My Account</span></a
                            >
                        </MenuItem>
                        <MenuItem v-slot="{ active }">
                            <a
                                href="javascript:void(0)"
                                :class="[
                                    active ? 'bg-gray-100 text-gray-900' : 'text-gray-700',
                                    'border-t border-basic-2 px-4 font-body-md-semibold leading-[3.5rem] flex gap-2 transition-all duration-500s hover:bg-background-basic-2',
                                ]"
                                @click="popup.showLogoutPopup = true">
                                <img src="@/assets/img/main-header-4.svg" alt="main-header-4" class="w-[15px] mx-[3px]"/><span>Log out</span></a
                            >
                        </MenuItem>
                    </div>
                </MenuItems>
            </transition>
        </Menu>
    </div>
    <Teleport to="body">
        <!-- My Account Popup -->
        <div
            v-if="popup.showMyAccountPopup"
            class="fixed top-0 left-0 w-full h-full bg-black/50 z-[80] flex items-center justify-center"
        >
            <div class="border rounded-lg bg-white w-[30rem]">
                <!-- Header -->
                <div class="px-6 py-4 w-full flex justify-between items-center">
                    <span class="font-heading-sm-semibold text-content-basic-1">
                        My Account
                    </span>
                    <button
                        type="button"
                        class="flex items-center justify-center"
                        @click="closeMyAccountPopup"
                    >
                        <CloseIcon :width="24" :height="24" color="text-content-basic-1" weight="REGULAR" />
                    </button>
                </div>

                <div class="w-full h-px bg-gray-200"></div>

                <!-- Body -->
                <div class="max-h-[80vh] overflow-y-auto">
                    <div class="px-8 py-6 space-y-6">
                        <!-- Account Information -->
                        <div class="space-y-2">
                            <p class="font-body-sm-medium text-content-basic-1">Account Information</p>
                            <div class="border border-border-basic-1 rounded-02 bg-white px-4 py-2 flex flex-col">
                                <div class="flex items-center justify-between">
                                    <div class="flex items-center gap-1">
                                        <span class="font-heading-sm-semibold text-content-basic-1">
                                            {{ username }}
                                        </span>
                                        <EmployeeStatusTag :statusCode="employeeStatus" />
                                    </div>
                                </div>
                                <div class="flex items-center gap-1">
                                    <MailIcon :width="16" :height="16" color="text-content-basic-2" weight="CLOSE" />
                                    <span class="font-body-sm-regular text-content-basic-2">
                                        {{ email }}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <!-- 진입점 리스트: Change Password / Multi-factor authentication -->
                        <div class="border border-border-basic-1 rounded-02 overflow-hidden">
                            <button
                                type="button"
                                class="w-full p-4 flex gap-1 items-center justify-stretch hover:bg-background-basic-2"
                                @click="openChangePasswordPopup"
                            >
                                <img src="@/assets/img/lock-icon.svg" alt="lock" />
                                <span class="text-left flex-1 font-body-md-regular text-content-basic-1">Change Password</span>
                                <ChevronRightIcon :width="16" :height="16" color="text-content-basic-2" weight="REGULAR" />
                            </button>

                            <button
                                type="button"
                                class="border-t border-t-border-basic-2 w-full p-4 flex gap-1 items-center justify-stretch hover:bg-background-basic-2"
                                @click="openMfaSetupPopup"
                            >
                                <img src="@/assets/img/security.svg" alt="lock" />
                                <span class="text-left flex-1 font-body-md-regular text-content-basic-1">Multi-factor authentication</span>
                                <ChevronRightIcon :width="16" :height="16" color="text-content-basic-2" weight="REGULAR" />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>


        <!-- Change Password Popup -->
        <div
            v-if="popup.showChangePasswordPopup"
            class="fixed top-0 left-0 w-full h-full bg-black/50 z-[80] flex items-center justify-center"
        >
            <div class="border rounded-lg shadow-modal bg-white w-[30rem]">
                <!-- Header -->
                <div class="px-6 py-4 w-full flex justify-between items-center">
                    <span class="font-heading-sm-semibold text-content-basic-1">Change Password</span>
                    <button
                        type="button"
                        class="flex items-center justify-center"
                        @click="closeChangePasswordPopup"
                    >
                        <CloseIcon :width="24" :height="24" color="text-content-basic-1" weight="REGULAR" />
                    </button>
                </div>

                <div class="w-full h-px bg-gray-200"></div>

                <!-- Body -->
                <!-- 입력은 DesignInput(디자인 시스템) 을 사용한다. password 타입일 때 show/hide 토글이
                    내장되어 있고, name 라벨은 DesignInput 에 없으므로 c_label 로 직접 붙인다. -->
                <div class="max-h-[80vh] overflow-y-auto px-8 py-6 flex flex-col gap-6">
                    <div class="w-full">
                        <h1 class="c_label">Current Password</h1>
                        <DesignInput
                            v-model="changePasswordForm.currentPassword"
                            class="mt-2"
                            width="w-full"
                            placeholder="Enter Password"
                            :error="changePasswordError.currentPassword"
                            inputtype="password"
                            autocomplete="current-password"
                        />
                    </div>

                    <div class="w-full">
                        <h1 class="c_label">New Password</h1>
                        <DesignInput
                            v-model="changePasswordForm.newPassword"
                            class="mt-2"
                            width="w-full"
                            placeholder="Enter Password"
                            :error="changePasswordError.newPassword"
                            inputtype="password"
                            autocomplete="new-password"
                        />
                        <!-- 정책 위반 시에만 허용 특수문자 목록을 함께 노출한다 -->
                        <div v-if="showAllowedSpecialChars" class="mt-1 font-caption-md-regular text-accent-red-3">
                            <p>Allowed special characters:</p>
                            <p class="font-semibold break-all">{{ allowedSpecialChars }}</p>
                        </div>
                    </div>

                    <div class="w-full">
                        <h1 class="c_label">Confirm New Password</h1>
                        <DesignInput
                            v-model="changePasswordForm.confirmNewPassword"
                            class="mt-2"
                            width="w-full"
                            placeholder="Enter Password"
                            :error="changePasswordError.confirmNewPassword"
                            inputtype="password"
                            autocomplete="new-password"
                        />
                    </div>
                </div>

                <div class="w-full h-px bg-gray-200"></div>

                <!-- Footer -->
                <div class="px-8 py-6 flex justify-end gap-2">
                    <DesignButton
                        class="w-20"
                        btnText="Cancel"
                        btnSize="MD"
                        btnType="SECONDARY"
                        :btnDisabled="false"
                        btn-variant="BORDER"
                        @click-event="closeChangePasswordPopup"
                    />
                    <DesignButton
                        class="w-20"
                        btnText="Change"
                        btnSize="MD"
                        btnType="PRIMARY"
                        :btnDisabled="!canSubmitChangePassword || submittingChangePassword"
                        @click-event="submitChangePassword"
                    />
                </div>
            </div>
        </div>

        <!-- MFA Setup Popup -->
        <div v-if="popup.showMFASetupPopup" class="fixed top-0 w-full h-full bg-black/50 z-[80] flex justify-center items-center">
            <div class="border rounded-lg shadow shadow-gray-100 bg-white w-[45rem]">
                <div class="px-6 py-4 w-full flex justify-between items-center">
                    <span class="font-heading-sm-semibold text-content-basic-1">
                        Setup Multi-Factor Authentication (MFA)
                    </span>
                    <button
                        type="button"
                        class="flex items-center justify-center"
                        @click="closeMfaSetupPopup"
                    >
                        <CloseIcon :width="24" :height="24" color="text-content-basic-1" weight="REGULAR" />
                    </button>
                </div>

                <div class="w-full h-px bg-gray-200"></div>

                <div class="max-h-[80vh] overflow-y-auto">
                    <div v-if="stepName==='MFA_SETUP'">
                        <div class="pt-8 px-8 pb-12 flex flex-col items-center gap-8">
                            <p class="font-heading-sm-medium text-content-basic-1 text-center">
                                Please select the MFA method you would like to setup.
                            </p>

                            <div class="flex justify-center w-full gap-4">
                                <div
                                    v-if="enabledTOTP"
                                    class="w-full border rounded-02 px-3 py-6 flex flex-col justify-between items-center gap-2"
                                >
                                    <div class="flex items-center justify-center p-2 rounded-03 bg-accent-violet-1">
                                        <DevicesIcon
                                            :width="32"
                                            :height="32"
                                            color="text-accent-violet-3"
                                        />
                                    </div>
                                    <div class="flex gap-2">
                                        <p class="font-body-md-semibold text-content-basic-1 text-center">
                                            Authenticator App
                                        </p>
                                        <designReadOnlyTag
                                            tagName="VERIFIED"
                                            v-if="verifiedTOTPMfa"
                                        >
                                            <template v-slot:front-icon>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                    <path fill-rule="evenodd" clip-rule="evenodd" d="M1.05002 5.9998C1.05002 3.266 3.2662 1.0498 6 1.0498C8.7338 1.0498 10.95 3.266 10.95 5.9998C10.95 8.73361 8.7338 10.9498 6 10.9498C3.2662 10.9498 1.05002 8.73361 1.05002 5.9998ZM8.32335 4.93772C8.49619 4.75915 8.49155 4.47426 8.31297 4.30141C8.13439 4.12857 7.84951 4.13321 7.67666 4.31179L5.33869 6.72727L4.32379 5.67586C4.15118 5.49705 3.8663 5.49202 3.68749 5.66462C3.50868 5.83723 3.50365 6.12211 3.67625 6.30092L5.01448 7.68728C5.09921 7.77506 5.21595 7.82467 5.33794 7.82476C5.45994 7.82484 5.57674 7.77538 5.66159 7.68772L8.32335 4.93772Z" fill="#2ABB5A"/>
                                                </svg>
                                            </template>
                                        </designReadOnlyTag>
                                    </div>
                                    <p class="font-body-sm-regular text-content-basic-2 text-center">
                                        Scan a QR code with an authenticator app to get OTP codes.
                                    </p>
                                    <DesignButton
                                        :btnText="verifiedTOTPMfa ? 'Reconfigure' : 'Set Up'"
                                        btnSize="MD"
                                        btnType="PRIMARY"
                                        :btnDisabled="false"
                                        @click-event="getOtpAuthUrl"
                                    />
                                </div>

                                <div class="w-full border rounded-02 px-3 py-6 flex flex-col justify-between items-center gap-2">
                                    <div class="flex items-center justify-center p-2 rounded-03 bg-accent-yellow-1">
                                        <MailIcon
                                            :width="32"
                                            :height="32"
                                            color="text-accent-yellow-3"
                                            weight="CLOSE"
                                        />
                                    </div>
                                    <p class="font-body-md-semibold text-content-basic-1 text-center">
                                        Email Verification
                                    </p>
                                    <p class="font-body-sm-regular text-content-basic-2 text-center">
                                        Receive verification codes via your registered email.
                                    </p>
                                    <DesignButton
                                        :btnText="verifiedEmailMfa ? 'Already verified' : 'Set Up'"
                                        btnSize="MD"
                                        btnType="PRIMARY"
                                        :btnDisabled="verifiedEmailMfa ? true : false"
                                        @click-event="setEmailMfa"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Auth app OTP -->
                    <div v-if="stepName==='AUTH_APP_SETUP'" class="w-full px-6 py-4">
                        <div class="flex items-center gap-1">
                            <ChevronLeft class="cursor-pointer" :width="20" :height="20" color="text-content-basic-1" weight="REGULAR" @click="goToMfaSetupStep" />
                            <p class="font-inter-heading-18 text-content-basic-1">Authenticator App</p>
                        </div>

                        <div class="flex flex-col justify-center items-center px-2 pt-6 pb-4 gap-8 rounded-03 w-full">
                            <div class="w-full flex items-start gap-4">
                                <DownloadIcon :width="24" :height="24" color="text-primary-3" />
                                <div>
                                    <p class="font-inter-heading-18 text-content-basic-1">Install an Authenticator App</p>
                                    <p class="font-inter-caption-3 text-content-basic-2">Install an authenticator app on your smartphone, such as Google Authenticator, Authy, or another TOTP app.</p>
                                </div>
                            </div>

                            <div class="w-full flex items-start gap-4">
                                <div>
                                    <PersonAddIcon :width="24" :height="24" color="text-primary-3" weight="REGULAR" />
                                </div>
                                <div>
                                    <p class="font-inter-heading-18 text-content-basic-1">Add Account to Authenticator App</p>
                                    <p class="font-inter-caption-3 text-content-basic-2">Open your authenticator app and scan the QR code below, or enter the setup key manually.</p>
                                    <div class="mt-4 p-4 w-full flex items-start rounded-02 bg-[#F4F4F4] gap-4">
                                        <div>
                                            <!-- QR Code Image 120x120 -->
                                            <img :src="mfaQrUrl" alt="QR Code" class="w-120 h-120" />
                                        </div>
                                        <div class="flex flex-col gap-2">
                                            <div class="flex items-center gap-1">
                                                <ErrorIcon :width="20" :height="20" color="text-primary-3" weight="REGULAR" />
                                                <p class="font-inter-heading-18 text-primary-3">Can't scan the QR code?</p>
                                            </div>
                                            <p class="font-inter-body-1 text-content-basic-1">Enter this setup key manually</p>
                                            <div class="w-96 p-2 flex gap-2 border rounded-lg bg-background-basic-1">
                                                <p class="font-inter-body-sm-regular text-content-basic-1 w-full">{{ groupBy4String(secretKey) }}</p>
                                                <img width="16" height="16" src="@/assets/img/content_copy.svg" class="cursor-pointer" @click="copyToClipboard(secretKey)" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="w-full flex items-start gap-4">
                                <div>
                                    <CheckCircleIcon :width="24" :height="24" color="text-primary-3" weight="REGULAR" />
                                </div>
                                <div class="flex-1">
                                    <p class="font-inter-heading-18 text-content-basic-1">Complete Setup</p>
                                    <p class="font-inter-caption-3 text-content-basic-2">Enter the 6-digit code displayed in your authenticator app to complete the setup.</p>
                                    <div class="mt-4 p-4 w-full items-start rounded-02 bg-[#F4F4F4] flex flex-col gap-2">
                                        <p class="font-inter-body-1 text-content-basic-1">6-digit code</p>
                                        <DesignInput
                                            v-model="otpCode"
                                            size="LG"
                                            width="w-[300px]"
                                            inputtype="text"
                                            :maxlength="6"
                                            placeholder="000000"
                                        />
                                        <p v-if="showExpireOtpErrorMessage" class="font-inter-caption-3 text-accent-red-3">Please request a new code. The verification code has expired.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="stepName==='AUTH_APP_COMPLETE_SETUP'">
                        <div class="flex flex-col justify-center items-center w-full h-[567px] p-8">
                            <CheckCircleIcon :width="32" :height="32" color="text-accent-green-3" weight="FILL" />
                            <p class="font-inter-heading-9 text-content-basic-1">OTP Setup</p>
                            <p class="font-inter-body-sm-regular text-content-basic-2">MFA setup completed successfully.</p>
                        </div>
                    </div>

                    <!-- Email MFA_SETUP -->
                    <div v-if="stepName==='EMAIL_MFA_SETUP'" class="w-full px-6 py-4">
                        <div class="flex items-center gap-1">
                            <ChevronLeft class="cursor-pointer" :width="20" :height="20" color="text-content-basic-1" weight="REGULAR" @click="goToMfaSetupStep" />
                            <p class="font-inter-heading-18 text-content-basic-1">Email Verification</p>
                        </div>

                        <div class="w-full h-[37.5rem] flex flex-col justify-center items-center py-6 gap-10">
                            <div class="flex flex-col gap-2">
                                <p class="font-inter-heading-18 text-content-basic-1">Email</p>
                                <DesignInput
                                    v-model="email"
                                    size="LG"
                                    width="w-96"
                                    inputtype="text"
                                    :disabled="true"
                                />
                                <DesignButton
                                    class="w-96"
                                    btnText="Send OTP"
                                    btnSize="LG"
                                    btnType="PRIMARY"
                                    :btnDisabled="sendedEmailOtp"
                                    @click-event="sendEmailOtp"
                                />
                            </div>

                            <div v-if="sendedEmailOtp" class="flex flex-col gap-2">
                                <div class="flex flex-col">
                                    <p class="font-inter-heading-18 text-content-basic-1">Complete Setup</p>
                                    <p class="font-inter-caption-3 text-content-basic-2">Please input the code you received in your Email.</p>
                                </div>
                                <div class="relative w-96 flex items-center">
                                    <DesignInput
                                        v-model="emailOtp"
                                        size="LG"
                                        width="w-96"
                                        inputtype="text"
                                        :maxlength="6"
                                        placeholder="6-digit code"
                                    />
                                    <span class="absolute right-3 top-1/2 -translate-y-1/2 font-inter-heading-20 text-accent-red-4">{{ emailRemainTime }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div v-if="stepName==='EMAIL_MFA_COMPLETE_SETUP'">
                        <div class="flex flex-col justify-center items-center w-full h-[567px] p-8">
                            <CheckCircleIcon :width="32" :height="32" color="text-accent-green-3" weight="FILL" />
                            <p class="font-inter-heading-9 text-content-basic-1">Email Verification Setup</p>
                            <p class="font-inter-body-sm-regular text-content-basic-2">MFA setup completed successfully.</p>
                        </div>
                    </div>
                </div>


                <div v-if="stepName!=='MFA_SETUP'" class="mt-3">
                    <div class="w-full h-px bg-gray-200"></div>
                    <div class="px-4 py-4 flex justify-end gap-2">
                        <DesignButton
                            v-if="stepName==='AUTH_APP_SETUP'"
                            class="w-20"
                            btnText="Verify"
                            btnSize="LG"
                            btnType="PRIMARY"
                            :btnDisabled="false"
                            @click-event="verifyOTP"
                        />
                        <DesignButton
                            v-if="stepName==='AUTH_APP_COMPLETE_SETUP'"
                            class="w-20"
                            btnText="OK"
                            btnSize="LG"
                            btnType="PRIMARY"
                            :btnDisabled="false"
                            @click-event="returnToMyAccountPopup"
                        />
                        <DesignButton
                            v-if="stepName==='EMAIL_MFA_SETUP'"
                            class="w-20"
                            btnText="Verify"
                            btnSize="LG"
                            btnType="PRIMARY"
                            :btnDisabled="false"
                            @click-event="verifyEmailOtp"
                        />
                        <DesignButton
                            v-if="stepName==='EMAIL_MFA_COMPLETE_SETUP'"
                            class="w-20"
                            btnText="OK"
                            btnSize="LG"
                            btnType="PRIMARY"
                            :btnDisabled="false"
                            @click-event="returnToMyAccountPopup"
                        />
                    </div>
                </div>
            </div>
        </div>

        <AppPopup v-model="popup.showLogoutPopup" name="Logout" button-text="OK" @button-event="logout" max-width="300px">
            <div class="p-6 px-3 space-y-6 text-center">Are you sure you want to log out?</div>
        </AppPopup>

        <!-- Auth App & Email  -->
        <DesignConfirmPopup
            v-model="showIncorrectOtpErrorMessage"
            :confirmDescription="'The verification code is incorrect.\nPlease try again.'"
            buttonText="OK"
        />

        <designDialogueModal
            v-model="showChangePasswordSuccess"
            dialogType="INFO"
            :modalTitle="'Your password has been\nchanged successfully.'"
            @confirm="()=>{showChangePasswordSuccess = false;}"
        />
        
        <!-- Email -->
        <DesignConfirmPopup
            v-model="showExpireEmailOtpErrorMessage"
            :confirmDescription="'Please request a new code.\nThe verification code has expired.'"
            buttonText="OK"
        />
    </Teleport>
</template>

<script setup>
import { inject, reactive, ref, computed, watch, onBeforeUnmount } from 'vue';
// import AppPopup from "@/components/AppPopup.vue";
// import PopupEmployeesInformation from "@/pages/employees/employees/popups/PopupEmployeesInformation.vue";
import { ChevronDownIcon } from '@heroicons/vue/solid';
import { CogIcon, LockClosedIcon, ShieldCheckIcon } from '@heroicons/vue/outline';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/vue';
import AppPopup from '@/components/AppPopup.vue';
import QRCode from 'qrcode';
import DashboardNotifications from '@/components/main/notification/DashboardNotifications.vue';
import ElementsTooltipSingleton from '@/components/elements/ElementsTooltipSingleton.vue';
import DashboardManual from '../DashboardManual/DashboardManual.vue';
import DesignButton from '@/components/design-system/design-button.vue';
import ChevronLeft from '@/components/design-system/icon/chevron-left.vue';
import ChevronRightIcon from '@/components/design-system/icon/chevron-right.vue';
import PersonIcon from '@/components/design-system/icon/person.vue';
import PersonAddIcon from '@/components/design-system/icon/person-add.vue';
import ErrorIcon from '@/components/design-system/icon/error.vue';
import CheckCircleIcon from '@/components/design-system/icon/check-circle.vue';
import DownloadIcon from '@/components/design-system/icon/download-icon.vue';
import DesignInput from '@/components/design-system/design-input.vue';
import DesignConfirmPopup from '@/components/design-system/design-confirm-popup.vue';
import CloseIcon from '@/components/design-system/icon/close.vue';
//import EmployeeStatusBadge from '@/components/employee/status-tag/employee-status-badge.vue';
import EmployeeStatusTag from '@/components/employee/status-tag/employee-status-tag.vue';
import MailIcon from '@/components/design-system/icon/mail.vue';
import DevicesIcon from '@/components/design-system/icon/devices.vue';
import { PasswordPolicyUtil } from '@/plugins/app-util';
import designDialogueModal from '@/components/design-system/design-dialogue-modal.vue';
import designReadOnlyTag from '@/components/design-system/design-read-only-tag.vue';
// const showMyProfile = ref(false);

if (!window.logOnProfile) {
    location.href = '/login';
}

const $api = inject('api');
const $axios = inject('axios');
// 230212 기획 요청으로 first name 으로 바꿈
const username = window.logOnProfile.data.employeeFirstName;
const profilePhoto = window.logOnProfile.data.profilePhotoUrl;
const employeeStatus = window.logOnProfile.data.employeeStatus;

// scroll lock 관련 상태
const scrollYBackup = ref(0);

const mfaQrUrl = ref('');
const secretKey = ref('');
const otpCode = ref('');
const email = window.logOnProfile.data.loginId;
const emailOtp = ref('');
const remainTime = ref(0);
const remainTimer = ref(null);
const sendedEmailOtp = ref(false);
const showExpireOtpErrorMessage = ref(false);
const showIncorrectOtpErrorMessage = ref(false);
const showExpireEmailOtpErrorMessage = ref(false);
const stepName = ref('MFA_SETUP');
const enabledTOTP = ref(false);
const verifiedTOTPMfa = ref(false);
const verifiedEmailMfa = ref(false);

// Change Password (HRRB-415) ------------------------------------------------------------------------
const changePasswordForm = reactive({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
});
const changePasswordError = reactive({
    currentPassword: '',
    newPassword: '',
    confirmNewPassword: '',
});
const showAllowedSpecialChars = ref(false);
const showChangePasswordSuccess = ref(false);
const submittingChangePassword = ref(false);
// 디자인 요구: Change 버튼은 3개 input 이 모두 채워졌을 때만 활성화
const canSubmitChangePassword = computed(() =>
    !!changePasswordForm.currentPassword
    && !!changePasswordForm.newPassword
    && !!changePasswordForm.confirmNewPassword
);
const allowedSpecialChars = PasswordPolicyUtil.ALLOWED_SPECIAL_CHARS.join(' ');
const POLICY_ERROR_MESSAGE =
    'Password must be 12-64 characters long and include uppercase, lowercase, number, and at least one special character.';

const popup = reactive({
    showLogoutPopup: ref(false),
    showMyAccountPopup: ref(false),
    showMFASetupPopup: ref(false),
    showChangePasswordPopup: ref(false),
});

const notificationList = reactive({
    show: ref(false),
    countNewNews: ref(0),
});

// computed → Composition API로 변환
const isAnyPopupOpen = computed(() =>
    popup.showLogoutPopup || popup.showMyAccountPopup || popup.showMFASetupPopup || popup.showChangePasswordPopup
    // 다른 팝업 추가하려면 || popup.XXXPopup
);

const emailRemainTime = computed(() => {
    const s = Math.max(0, Math.floor(remainTime.value));
    const m = String(Math.floor(s / 60)).padStart(2, '0');
    const sec = String(s % 60).padStart(2, '0');
    return `${m}:${sec}`;
});

function lockBodyScroll() {
    scrollYBackup.value = window.scrollY || document.documentElement.scrollTop || 0;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollYBackup.value}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.width = '100%';
    document.body.style.overflow = 'hidden';
}

function unlockBodyScroll() {
    const y = scrollYBackup.value || 0;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    window.scrollTo(0, y);
}

// watch 로직
watch(isAnyPopupOpen, (val) => {
    if (val) lockBodyScroll();
    else unlockBodyScroll();
});

// 페이지 벗어날 때 혹시 모를 잔여 스타일 복구
onBeforeUnmount(() => {
    unlockBodyScroll();
});

function groupBy4String(str) {
    return (str || '')
        .toString()
        .replace(/\s+/g, '')
        .match(/.{1,4}/g)
        ?.join(' ') ?? '';
}

function copyToClipboard() {
    navigator.clipboard.writeText(secretKey.value)
        .then(() => alert('Setup key copied to clipboard!'))
        .catch((err) => console.error('Failed to copy text: ', err));
}

function resetMfaState() {
    stepName.value = 'MFA_SETUP';

    // Auth app screens
    otpCode.value = '';
    mfaQrUrl.value = '';
    secretKey.value = '';

    // Email screens
    emailOtp.value = '';
    if (remainTimer.value) {
        clearInterval(remainTimer.value);
        remainTimer.value = null;
    }
    remainTime.value = 0;
    sendedEmailOtp.value = false;
}

function resetErrorMessages() {
    showIncorrectOtpErrorMessage.value = false;
    showExpireOtpErrorMessage.value = false;
    showExpireEmailOtpErrorMessage.value = false;
}

async function getOtpAuthUrl() {
    try {
        const url = $api('uri', 'get-generate-otp-auth-url');
        const res = await $axios.get(url);
        const data = res.data.data;

        try {
            const qrDataUrl = await QRCode.toDataURL(data.otpAuthUrl);
            mfaQrUrl.value = qrDataUrl;
        } catch (err) {
            console.error('fail to generate QR code', err);
            return;
        }

        secretKey.value = data.secretKey;
        stepName.value = 'AUTH_APP_SETUP';
    } catch (err) {
        console.error(err);
    }
}

async function openMyAccountPopup() {
    try {
        // MFA 등록 정보 조회 로직 구현
        let url = $api('uri', 'get-register-mfa-info');
        const res = await $axios.get(url);
        enabledTOTP.value = res.data.data.enabledTOTP;
        verifiedTOTPMfa.value = res.data.data.hasTOTP;
        verifiedEmailMfa.value = res.data.data.verifiedEmailMfa;
        popup.showMyAccountPopup = true;
    }catch(err){
        console.error(err);
    }
}

function closeMyAccountPopup() {
    popup.showMyAccountPopup = false;
    resetMfaState();
    resetErrorMessages();
}

function openMfaSetupPopup() {
  popup.showMyAccountPopup = false;
  popup.showMFASetupPopup = true;
  stepName.value = 'MFA_SETUP';
}

function closeMfaSetupPopup() {
    popup.showMFASetupPopup = false;
    resetMfaState();
    resetErrorMessages();
}

function goToMfaSetupStep() {
    resetMfaState();
    resetErrorMessages();
}

function returnToMyAccountPopup() {
    popup.showMFASetupPopup = false;
    resetMfaState();
    resetErrorMessages();
    openMyAccountPopup();
}

// Change Password (HRRB-415) ------------------------------------------------------------------------

function clearChangePasswordErrors() {
    changePasswordError.currentPassword = '';
    changePasswordError.newPassword = '';
    changePasswordError.confirmNewPassword = '';
    showAllowedSpecialChars.value = false;
}

function resetChangePasswordState() {
    changePasswordForm.currentPassword = '';
    changePasswordForm.newPassword = '';
    changePasswordForm.confirmNewPassword = '';
    clearChangePasswordErrors();
    submittingChangePassword.value = false;
}

function openChangePasswordPopup() {
    popup.showMyAccountPopup = false;
    resetChangePasswordState();
    popup.showChangePasswordPopup = true;
}

/** Cancel / X: 디자인 요구대로 이전 My Account 모달로 되돌린다 */
function closeChangePasswordPopup() {
    popup.showChangePasswordPopup = false;
    resetChangePasswordState();
    openMyAccountPopup();
}

/**
 * 제출 전 클라이언트 검증.
 *
 * 에러는 화면의 필드 순서(Current -> New -> Confirm)대로 위에서 아래로 하나만 보여준다.
 * 그래서 여기서는 빈 값만 본다(Change 버튼이 비활성이라 실제로는 거의 발생하지 않는 방어 코드).
 * 현재 비밀번호 불일치 / 현재와 동일 / 정책 위반 / 최근 재사용 / 확인값 불일치는
 * 모두 서버가 디자인이 정한 좌->우 순서대로 판정한다.
 * (클라이언트에서 미리 막으면 그 위 필드인 Current Password 의 불일치 에러가 영원히 표시되지 않는다)
 */
function validateChangePassword() {
    clearChangePasswordErrors();

    if (!changePasswordForm.currentPassword) {
        changePasswordError.currentPassword = 'Please provide your current password.';
        return false;
    }

    if (!changePasswordForm.newPassword) {
        changePasswordError.newPassword = 'Please provide your new password.';
        return false;
    }

    if (!changePasswordForm.confirmNewPassword) {
        changePasswordError.confirmNewPassword = 'Please confirm your new password.';
        return false;
    }

    return true;
}

function applyChangePasswordServerError(err) {
    const data = err?.response?.data;
    const errorCode = data?.code;

    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_403_REUSED_FAILED') {
        // 재사용 차단 개수는 회사별 보안정책 값이므로 응답 값으로 문구를 채운다
        const blockCount = data?.data?.passwordReuseBlockCount;
        changePasswordError.newPassword = blockCount
            ? `This password was used recently. Choose a password different from your last ${blockCount} passwords.`
            : 'This password was used recently. Choose a different password.';
        return;
    }

    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_403_MISMATCH_FAILED') {
        changePasswordError.currentPassword = 'The current password does not match. Please try again.';
        return;
    }

    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_400_SAME_AS_CURRENT_FAILED') {
        changePasswordError.newPassword = 'The new password must be different from the current password.';
        return;
    }

    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_400_POLICY_FAILED') {
        changePasswordError.newPassword = POLICY_ERROR_MESSAGE;
        showAllowedSpecialChars.value = true;
        return;
    }

    if (errorCode === 'HR_EMPLOYEE_PASSWORD_UPDATE_400_CONFIRM_MISMATCH_FAILED') {
        changePasswordError.confirmNewPassword = 'The confirmation password does not match.';
        return;
    }

    changePasswordError.newPassword = 'Failed to change the password. Please try again.';
}

async function submitChangePassword() {
    if (submittingChangePassword.value) return;
    if (!validateChangePassword()) return;

    submittingChangePassword.value = true;
    try {
        const url = $api('uri', 'put-password');
        await $axios.put(url, {
            currentPassword: changePasswordForm.currentPassword,
            newPassword: changePasswordForm.newPassword,
            confirmNewPassword: changePasswordForm.confirmNewPassword,
        });

        popup.showChangePasswordPopup = false;
        resetChangePasswordState();
        showChangePasswordSuccess.value = true;
    } catch (err) {
        applyChangePasswordServerError(err);
    } finally {
        submittingChangePassword.value = false;
    }
}

async function verifyOTP() {
    resetErrorMessages();
    try {
        const url = $api('uri', 'post-verify-otp');
        const res = await $axios.post(url, {
                otpCode: otpCode.value,
                isSetup: true,
                });

        const incorrectOtp = res.data.data.incorrectOtp;
        const previousWindow = res.data.data.previousWindow;

        if (previousWindow) {
            showExpireOtpErrorMessage.value = true;
            return;
        }

        if (incorrectOtp) {
            showIncorrectOtpErrorMessage.value = true;
            return;
        }
        
        stepName.value = 'AUTH_APP_COMPLETE_SETUP';
    } catch (err) {
        console.error(err);
    }
}

function setEmailMfa() {
    stepName.value = 'EMAIL_MFA_SETUP';
}

async function sendEmailOtp() {
    resetErrorMessages();
    try {
        const url = $api('uri', 'post-send-email-otp');
        await $axios.post(url, { otpCode: otpCode.value });

        remainTime.value = 300; // 5분
        remainTimer.value = setInterval(() => {
        remainTime.value -= 1;
        if (remainTime.value <= 0) {
            remainTime.value = 0;
            clearInterval(remainTimer.value);
            remainTimer.value = null;
        }
        }, 1000);
        sendedEmailOtp.value = true;
    } catch (err) {
        console.error(err);
    }
}

async function verifyEmailOtp() {
    if (remainTime.value <= 0) {
        emailOtp.value = null;
        sendedEmailOtp.value = false;
        stepName.value = 'EMAIL_MFA_SETUP';
        showExpireEmailOtpErrorMessage.value = true;
        return;
    }

    try {
        const url = $api('uri', 'post-email-verify-otp');
        const res = await $axios.post(url, {
        otpCode: emailOtp.value,
        isSetup: true, // Email은 항상 true
        });

        const isInvalid = res.data.data.isInvalid;
        if (isInvalid) {
            showIncorrectOtpErrorMessage.value = true;
            return;
        }

        stepName.value = 'EMAIL_MFA_COMPLETE_SETUP';
    } catch (err) {
        console.error(err);
    }
}

function logout() {
    const url = $api('uri', 'post-sign-out');

    $axios
        .post(url)
        .then((res) => {
            location.href = '/login';
            console.log('res : ', res.data);
        })
        .catch((err) => {
            alert('Sign out failed. Please sign out again.');
            console.error(err);
        });
}


function clickTestingTools() {
    location.href = '/test_tools/message_q';
}

let profileString = username.substr(0, 1);

function enableTestTools() {
    return !(process.env.VUE_APP_SERVER_MODE === 'prd');
    // return true;
}

let __tooltipComponent__hidden__ = null;

function loadTooltipElementReady(ele) {
    if (ele == null) return; // auto-reload, so element can be null sometime.
    __tooltipComponent__hidden__ = ele;
}

function loadTooltip(ele, msgFn) {
    __tooltipComponent__hidden__.loadBasic(ele, msgFn);
}

let showManualOver = ref(false);

function clickViewManual() {
    showManualOver.value = true;
}
</script>
